import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

import {
  ADVANCED_SEARCH,
  DEALER_SCOPE_ADVANCED_SEARCH,
  DEALER_SEARCH_KEY,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import {
  DealerLocalParams,
  searchDealerWithQuery,
} from '@/app/components/shared/dealer/utils/dealer-search';
import {
  groupingDealersForSearch,
  isDealerCV,
} from '@/app/components/shared/dealer/utils/mapping-dealer';
import type {
  DealerPlace,
  DealerSearch,
} from '@/app/components/shared/dealer/utils/types';
import { safeCall } from '@/app/utils/safeCall';
import { DealerGrouping } from '@/services/contentstack/models/dealer';

export const useDealerSearchKey = () => {
  const searchParams = useSearchParams();

  const allSearchKey = useMemo(() => {
    return {
      [DEALER_SEARCH_KEY.Tab]: searchParams?.get(DEALER_SEARCH_KEY.Tab),
      [DEALER_SEARCH_KEY.Q]: searchParams?.get(DEALER_SEARCH_KEY.Q),
      [DEALER_SEARCH_KEY.Lat]: searchParams?.get(DEALER_SEARCH_KEY.Lat),
      [DEALER_SEARCH_KEY.Lng]: searchParams?.get(DEALER_SEARCH_KEY.Lng),
      [DEALER_SEARCH_KEY.VehicleType]: searchParams?.get(
        DEALER_SEARCH_KEY.VehicleType
      ),
    } as Record<DEALER_SEARCH_KEY, string | null>;
  }, [searchParams]);

  const initVehicleType = (uid?: string) => {
    if (!allSearchKey[DEALER_SEARCH_KEY.VehicleType]) {
      const currentParams = new URLSearchParams(searchParams?.toString());
      const value = isDealerCV ? 'cv' : 'lcv';
      currentParams.set(DEALER_SEARCH_KEY.VehicleType, value);
      const url = uid ? `/dealer/search/${uid}` : '/dealer/search';

      window.history.replaceState(
        null,
        '',
        `${url}?${currentParams.toString()}`
      );

      return value;
    }
  };

  const setToggleSearchKey = (
    type: string,
    isSelected: boolean,
    setSelectedOptions?: (prev: any) => void
  ) => {
    const currentParams = new URLSearchParams(searchParams?.toString());

    if (isSelected) {
      safeCall(setSelectedOptions, (prev) => {
        const newSet = new Set(prev);
        newSet.add(type);
        return newSet;
      });
      if (!currentParams.getAll(DEALER_SEARCH_KEY.ServiceType).includes(type)) {
        currentParams.append(DEALER_SEARCH_KEY.ServiceType, type);
      }
    } else {
      safeCall(setSelectedOptions, (prev) => {
        const newSet = new Set(prev);
        newSet.delete(type);
        return newSet;
      });
      currentParams.delete(DEALER_SEARCH_KEY.ServiceType, type);
      currentParams.delete(DEALER_SEARCH_KEY.Service);
    }

    setTimeout(() => {
      window.history.replaceState(
        null,
        '',
        `/dealer/search?${currentParams.toString()}`
      );
    }, 0);
  };

  const setSelectDealerKey = (dealer: DealerSearch, tab?: string) => {
    const currentParams = new URLSearchParams(searchParams?.toString());

    if (tab) {
      currentParams.set(DEALER_SEARCH_KEY.Tab, tab);
    } else if (allSearchKey[DEALER_SEARCH_KEY.Tab]) {
      currentParams.delete(DEALER_SEARCH_KEY.Tab);
    }

    setTimeout(() => {
      window.history.replaceState(
        null,
        '',
        `/dealer/search/${dealer.uid}?${currentParams.toString()}`
      );
    }, 0);
  };

  const getSelectDealerUrl = (
    dealer: DealerSearch,
    tab?: string,
    searchValue?: string
  ) => {
    const newParams = new URLSearchParams(searchParams?.toString());
    if (tab) {
      newParams.set(DEALER_SEARCH_KEY.Tab, tab);
    }
    if (searchValue) {
      newParams.set(DEALER_SEARCH_KEY.Q, searchValue);
    }

    const url = `/dealer/search/${dealer.uid}?${newParams.toString()}`;
    return url;
  };

  const removeSearchLocation = (replaceState = true) => {
    const currentParams = new URLSearchParams(searchParams?.toString());
    const searchKeys = [DEALER_SEARCH_KEY.Tab, DEALER_SEARCH_KEY.Q];

    for (const param of searchKeys) {
      if (allSearchKey[param]) {
        currentParams.delete(param);
      }
    }

    if (!replaceState) return currentParams;

    setTimeout(() => {
      window.history.replaceState(
        null,
        '',
        `/dealer/search?${currentParams.toString()}`
      );
    }, 0);
  };

  const getAdvancedSearchKey = (setSelectedOptions: (prev: any) => void) => {
    if (!searchParams || searchParams.size === 0) return;

    for (const [key, value] of searchParams?.entries()) {
      setSelectedOptions((prev: any) => {
        if (!DEALER_SCOPE_ADVANCED_SEARCH.includes(key as DEALER_SEARCH_KEY)) {
          return prev;
        }
        const newSet = new Set(prev);
        if (!newSet.has(value)) {
          newSet.add(value);
        }
        return newSet;
      });
    }
  };

  const setDealerSearchResult = (item: DealerGrouping) => {
    const currentParams = new URLSearchParams(searchParams?.toString());

    if (item.groupForSearch?.type) {
      currentParams.set(DEALER_SEARCH_KEY.Tab, item.groupForSearch.type);
    }

    return `/dealer/search/${item.uid}?${currentParams.toString()}`;
  };

  const setSeachSuggestLocation = (
    value: DealerPlace,
    searchParams?: string
  ) => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set(DEALER_SEARCH_KEY.Lat, `${value.lat}`);
    currentParams.set(DEALER_SEARCH_KEY.Lng, `${value.lng}`);
    currentParams.set(DEALER_SEARCH_KEY.Q, value.name);

    return `${currentParams.toString()}`;
  };

  const handleAdvancedSearch = (options: string[]) => {
    const currentParams = new URLSearchParams();

    if (options.includes(DEALER_SEARCH_KEY.Service)) {
      currentParams.append(
        DEALER_SEARCH_KEY.ServiceType,
        DEALER_SEARCH_KEY.Service
      );
    }

    if (searchParams?.has(DEALER_SEARCH_KEY.Q)) {
      currentParams.append(
        DEALER_SEARCH_KEY.Q,
        searchParams.get(DEALER_SEARCH_KEY.Q) || ''
      );
    }

    for (const option of options) {
      const findGroup = ADVANCED_SEARCH.find((item) => {
        if (item.options) {
          return item.options.some((itemOption) => itemOption.name === option);
        }

        return item.id === option;
      });

      if (!findGroup) continue;

      const groupId = findGroup.id as DEALER_SEARCH_KEY;

      if (
        [DEALER_SEARCH_KEY.Showroom, DEALER_SEARCH_KEY.BodyPaint].includes(
          groupId
        )
      ) {
        currentParams.append(DEALER_SEARCH_KEY.ServiceType, option);
      } else if (groupId === DEALER_SEARCH_KEY.BusinessDay) {
        currentParams.append(DEALER_SEARCH_KEY.BusinessDay, option);
      } else {
        currentParams.append(groupId, option);
      }
    }

    return currentParams;
  };

  const setTextSearchParams = (value: string, shouldSetQ = true) => {
    const currentParams = new URLSearchParams(searchParams?.toString());

    currentParams.delete(DEALER_SEARCH_KEY.Lat);
    currentParams.delete(DEALER_SEARCH_KEY.Lng);

    if (shouldSetQ) {
      currentParams.set(DEALER_SEARCH_KEY.Q, value);
    }

    return currentParams;
  };

  const removeTextSearchParams = (
    searchValue?: string,
    replaceState = true
  ) => {
    const currentParams = new URLSearchParams(searchParams?.toString());

    if (!searchValue && currentParams.has(DEALER_SEARCH_KEY.Q)) {
      currentParams.delete(DEALER_SEARCH_KEY.Q);

      if (currentParams.has(DEALER_SEARCH_KEY.Lat)) {
        currentParams.delete(DEALER_SEARCH_KEY.Lat);
      }

      if (currentParams.has(DEALER_SEARCH_KEY.Lng)) {
        currentParams.delete(DEALER_SEARCH_KEY.Lng);
      }

      if (!replaceState) return currentParams;

      setTimeout(() => {
        window.history.replaceState(
          null,
          '',
          `/dealer/search?${currentParams.toString()}`
        );
      }, 0);
    }
  };

  const filterSearchAutoComplete = (
    items: DealerGrouping[],
    value: string,
    filterSearch?: DealerLocalParams
  ) => {
    return searchDealerWithQuery(groupingDealersForSearch(items), value, {
      ...Object.fromEntries(searchParams?.entries() || []),
      ...filterSearch,
    });
  };

  return {
    initVehicleType,
    setToggleSearchKey,
    setSelectDealerKey,
    setDealerSearchResult,
    setTextSearchParams,
    getSelectDealerUrl,
    getAdvancedSearchKey,
    setSeachSuggestLocation,
    handleAdvancedSearch,
    removeSearchLocation,
    removeTextSearchParams,
    allSearchKey,
    filterSearchAutoComplete,
  };
};
